import { useCallback, useEffect, useState } from 'react';
import { getDepositoTabelaPrecoClientes } from '@spark/services/deposito';
import { notification } from '@spark/ui-components/utils/notification';
import { toApiDate } from '@spark/utils/formatters';

const normalizeFilters = (filtros) => (
  Object.fromEntries(
    Object.entries(filtros)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [key, value instanceof Date ? toApiDate(value) : value])
  )
);

export const useDepositoTabelaPrecoClientes = (show, idTabelaPrecoDeposito) => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({});
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [quantidadePorPagina, setQuantidadePorPagina] = useState(10);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const carregarClientes = useCallback(async () => {
    if (!show) return;
    if (!idTabelaPrecoDeposito) {
      setClientes([]);
      setTotalRegistros(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = {
        PaginaAtual: paginaAtual,
        QuantidadePorPagina: quantidadePorPagina,
        IdTabelaPrecoDeposito: idTabelaPrecoDeposito,
        ...normalizeFilters(filtros)
      };

      const response = await getDepositoTabelaPrecoClientes(params);
      const data = response?.data || response || {};
      const registros = Array.isArray(data.registros) ? data.registros : [];

      setClientes(registros);
      setTotalRegistros(data.totalRegistros || registros.length);
    } catch (error) {
      console.error('Erro ao carregar clientes da tabela de preco:', error);
      notification.error(
        error?.message || 'Nao foi possivel carregar os clientes da tabela de preco',
        'Erro ao carregar clientes'
      );
      setClientes([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  }, [show, idTabelaPrecoDeposito, filtros, paginaAtual, quantidadePorPagina]);

  useEffect(() => {
    if (show) {
      carregarClientes();
    }
  }, [show, carregarClientes]);

  useEffect(() => {
    if (show) {
      setPaginaAtual(1);
    }
  }, [idTabelaPrecoDeposito, show]);

  const handleFiltrosChange = useCallback((novosFiltros) => {
    setFiltros(novosFiltros);
    setPaginaAtual(1);
  }, []);

  const handlePaginationChange = useCallback((paginationState) => {
    if (paginationState.pageSize !== quantidadePorPagina) {
      setQuantidadePorPagina(paginationState.pageSize);
      setPaginaAtual(1);
    } else if (paginationState.pageIndex !== undefined) {
      setPaginaAtual(paginationState.pageIndex + 1);
    }
  }, [quantidadePorPagina]);

  const resetFiltros = useCallback(() => {
    setFiltros({});
    setPaginaAtual(1);
    setQuantidadePorPagina(10);
  }, []);

  const reload = useCallback(() => {
    carregarClientes();
  }, [carregarClientes]);

  return {
    clientes,
    loading,
    filtros,
    paginaAtual,
    quantidadePorPagina,
    totalRegistros,
    handleFiltrosChange,
    handlePaginationChange,
    resetFiltros,
    reload
  };
};
